import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { NewsAndGuidesDetailsPageCombinedState, NewsAndGuidesDetailsPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { NewsAndGuidesDetailsPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<NewsAndGuidesDetailsPageCombinedState> };

type Props = {
	countryCode: string;
	slug: string;
	prefetchedData: NewsAndGuidesDetailsPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const NewsAndGuidesDetailsPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	countryCode,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(countryCode, slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	const { details } = pageData.data;
	const petitionIds =
		details.__typename === 'NewsPost' ? details.relatedPetitionIds.map((id) => `petition_${id}`).join(',') : '';

	return (
		<NewsAndGuidesDetailsPageDataContextProvider pageData={pageData}>
			<TrackingContext
				properties={{
					date: details.createdAt,
					petition_ids: petitionIds || undefined,
					post_id: `post_${details.id}`,
					slug: details.slug,
				}}
			>
				{children}
			</TrackingContext>
		</NewsAndGuidesDetailsPageDataContextProvider>
	);
};
